import { useMemo } from 'react';
import { ErrorState, getErrorMessageFromUnknown, Notice, ProcessedError } from '@/core';
import { useAreQueriesCompatibleWithDataType } from '@/plugin-system/components/VizPanel/utils';
import { useVizPanelNotices } from '@/plugin-system/components/VizPanelContextProvider';
import { useAppInteractionLevel } from '@/plugin-system/runtime/AppMetadataProvider';
import { useDataQueryNotices } from '@/plugin-system/runtime/DataQueriesProvider';
import { useVizContainerPanelDefinition } from './useVizContainerPanelDefinition';

export const INCOMPATIBLE_DATASOURCE_ERROR_MESSAGE: Error = {
  name: 'Incompatible Datasource Error',
  message: 'Datasource is not compatible with the panel type. Please delete the query and try again.',
};

export type CombinedVizContainerErrors = {
  /**
   * Combined array of errors for the `VizContainer`. Errors are de-duplicated and processed for ease of
   * consistently presenting the error messages in the UI.
   */
  errors: ProcessedError[];
  /**
   * Indicates the overall error state of the `VizContainer`.
   */
  errorState: ErrorState;
};

export type UseVizContainerErrorsErrorsCallback = (errors: CombinedVizContainerErrors) => CombinedVizContainerErrors;
export type UseVizContainerErrorsNoticesCallback = (notices: Notice[]) => Notice[];

export interface UseVizContainerErrorsAndNotices {
  /**
   * Controls error state and how it is presented to the user.
   *
   * This prop supports three different ways of setting the error state:
   * - `undefined` (default behavior): Error state is determined by a combination of errors from `DataQueriesProvider`.
   * - `CombinedVizContainerErrors`: Explicitly sets the error state. Useful in cases where you are not relying on error
   *   state from `DataQueriesProvider`.
   * - `function`: Callback function that provides the default error state as an argument and returns the error state
   *   that will be used. This is useful in mixed cases where you want to combine the error state
   *   from a `DataQueriesProvider` with some additional custom logic.
   */
  errors?: CombinedVizContainerErrors | UseVizContainerErrorsErrorsCallback;

  /**
   * Controls notices presented to the user.
   *
   * This prop supports three different ways of setting the notice state:
   * - `undefined` (default behavior): Notice state is determined by a combination of notices from `DataQueriesProvider`
   *    and the `useVizPanelNotices` API.
   * - `Notice[]`: Explicitly sets the notice state. Useful in cases where you are not relying on notice state from
   *   `DataQueriesProvider` and `useVizPanelNotices`.
   * - `function`: Callback function that provides the default notice state as an argument and returns the notice state
   *   that will be used. This is useful in mixed cases where you want to combine the notice state
   *   from a `DataQueriesProvider` and `useVizPanelNotices` with some additional custom logic.
   */
  notices?: Notice[] | UseVizContainerErrorsNoticesCallback;
}

/**
 * Hook that helps consistently set the `errors` and `notices` states on `VizContainer` subcomponents (e.g.
 * `VizContainer.Header`, `VizContainer.VizErrorBoundary`).
 *
 * See the jsdoc on the props to understand the different options for configuring the error and notice state.
 */
export function useVizContainerErrorsAndNotices({
  notices: noticesProp,
  errors: errorsProp,
}: UseVizContainerErrorsAndNotices = {}) {
  const {
    isFetching,
    isFetchingMore,
    isLoading,
    errors: dataQueryErrors,
    errorState: dataQueryErrorState,
    notices: queryNotices,
  } = useDataQueryNotices();

  const { panelNotices } = useVizPanelNotices();
  const appInteractionLevel = useAppInteractionLevel();

  const panelDefinition = useVizContainerPanelDefinition();

  const areQueriesCompatibleWithDataType = useAreQueriesCompatibleWithDataType(panelDefinition);

  const combinedErrors: CombinedVizContainerErrors = useMemo(() => {
    if (errorsProp && typeof errorsProp === 'object') {
      // If errors prop is an object, use that.
      return errorsProp;
    }

    // Initialize with error information from data queries.
    let errorState: CombinedVizContainerErrors['errorState'] = dataQueryErrorState;

    let errors = [...dataQueryErrors];

    // Panel is fully broken when there are incompatible data types.
    if (!areQueriesCompatibleWithDataType) {
      errorState = 'critical';
      errors = [INCOMPATIBLE_DATASOURCE_ERROR_MESSAGE, ...errors];
    }

    // Use a map to de-dupe the error messages and keep a count. Using a `Map` instead of an
    // object because `Map` retains order of insertion, so we keep errors in order.
    const errorMap = new Map<string, number>();
    for (const error of errors) {
      const errorMsg = getErrorMessageFromUnknown(error);

      const errorCount = errorMap.get(errorMsg) ?? 0;
      errorMap.set(errorMsg, errorCount + 1);
    }

    // Transform the error map into an array of processed errors.
    const processedErrors: ProcessedError[] = Array.from(errorMap.entries()).map(([errorMessage, count]) => {
      return {
        errorMessage,
        count,
      };
    });

    const defaultErrorState = {
      errors: processedErrors,
      errorState,
    };

    if (typeof errorsProp === 'function') {
      // If errors prop is a function, pass it the default value and return the response.
      return errorsProp(defaultErrorState);
    }

    // Errors prop is not set, use the default.
    return defaultErrorState;
  }, [errorsProp, areQueriesCompatibleWithDataType, dataQueryErrorState, dataQueryErrors]);

  const combinedNotices: Notice[] = useMemo(() => {
    if (Array.isArray(noticesProp)) {
      // If notices prop is an array of notices use that.
      return noticesProp;
    }

    const defaultNotices = [...queryNotices, ...panelNotices].filter((notice) => {
      // Filter out notices that are not relevant for the given interaction level.
      // When `appInteractionLevels` is not set, the notice is always shown.
      return !notice.appInteractionLevels || notice.appInteractionLevels.includes(appInteractionLevel);
    });

    if (typeof noticesProp === 'function') {
      // If notices prop is a function, pass it the default value and return the response.
      return noticesProp(defaultNotices);
    }

    // Notices prop is not set, use the default.
    return defaultNotices;
  }, [noticesProp, queryNotices, panelNotices, appInteractionLevel]);

  return {
    errors: combinedErrors,
    isFetching,
    isFetchingMore,
    isLoading,
    notices: combinedNotices,
  };
}
